import { memo, useEffect, useRef, useState } from "react";

// 3rd Party Libraries
import PropTypes from "prop-types";

// Components
import Input from '../form/input.jsx';

//Styles
import styles from "./style.module.scss";

export function Multipledatepicker({
  value = [],
  onChange = () => {},
  label = "",
}) {
  const [isCalendarVisible, setCalendarVisible] = useState(false);
  const [selectedDates, setSelectedDates] = useState(value);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [inputValue, setInputValue] = useState("");
  const wrapperRef = useRef(null);

  const toggleDatePicker = () => {
    setCalendarVisible((prev) => !prev);
  };

  const handleDayClick = (day) => {
    const dayIndex = selectedDates.indexOf(day);
    let updatedDates;
    if (dayIndex > -1) {
      updatedDates = selectedDates.filter((_, index) => index !== dayIndex);
    } else {
      updatedDates = [...selectedDates, day];
    }
    setSelectedDates(updatedDates);
    setInputValue(updatedDates.join(", "));
    onChange(updatedDates);
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setCalendarVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderCalendar = () => {
    const daysContainer = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay(); // Day of the week the month starts on
    const lastDate = new Date(year, month + 1, 0).getDate(); // Last date of the month

    let week = [];

    // Add empty placeholders for days before the 1st of the month
    for (let i = 0; i < firstDay; i++) {
      week.push(
        <div
          key={`empty-${i}`}
          className={`${styles["rmdp-day"]} ${styles["rmdp-day-hidden"]}`}
        ></div>
      );
    }

    // Generate the days of the month
    for (let day = 1; day <= lastDate; day++) {
      const isSelected = selectedDates.includes(day);
      const isToday =
        day === new Date().getDate() &&
        currentDate.getMonth() === new Date().getMonth() &&
        currentDate.getFullYear() === new Date().getFullYear();

      week.push(
        <div
          key={day}
          className={`${styles["rmdp-day"]} ${
            isSelected ? styles["rmdp-selected"] : ""
          } ${isToday ? styles["rmdp-today"] : ""}`}
          onClick={() => handleDayClick(day)}
        >
          <span>{day}</span>
        </div>
      );

      // Wrap each week in a div with class "rmdp-weeks"
      if (week.length === 7 || day === lastDate) {
        daysContainer.push(
          <div key={`week-${day}`} className={styles["rmdp-weeks"]}>
            {week}
          </div>
        );
        week = []; // Reset for the next week
      }
    }

    return daysContainer;
  };

  const prevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  useEffect(() => {
    setInputValue(selectedDates);
  }, [selectedDates]);

  return (
    <div className={styles["rmdp-container"]} ref={wrapperRef}>
      <div>
        <div>
          <Input
            label={label}
            type="text"
            id="dataImpMultiDatePickerInput"
            className={styles["rmdp-input-multiple"]}
            value={inputValue}
            readOnly
            onClick={toggleDatePicker}
            endIconImage={
              <svg
                id="dataImpMultiDatePickerSvg"
                className={`${styles["icon-tabler"]}`}
                data-testid="calendarIcon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                onClick={toggleDatePicker}
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <rect x="4" y="5" width="16" height="16" rx="2"></rect>
                <line x1="16" y1="3" x2="16" y2="7"></line>
                <line x1="8" y1="3" x2="8" y2="7"></line>
                <line x1="4" y1="11" x2="20" y2="11"></line>
                <rect x="8" y="15" width="2" height="2"></rect>
              </svg>
            }
          />
        </div>
      </div>
      {isCalendarVisible && (
        <div
          className={`${styles["rmdp-wrapper1"]} ${styles["rmdp-shadow"]} d-block`}
        >
          <div className="d-flex ">
            <div className={styles["rmdp-calendar"]}>
              <div className={styles["rmdp-header"]}>
                <div className={styles["rmdp-btn"]}>
                  <button
                    type="button"
                    disabled
                    className={`${styles["rmdp-arrow-container"]} ${styles["rmdp-left"]}`}
                    onClick={prevMonth}
                  >
                    <i className={styles["rmdp-arrow"]}></i>
                  </button>
                  <div className={styles["rmdp-header-values"]}>
                    <span id="monthYear">
                      {currentDate.toLocaleString("default", { month: "long" })}{" "}
                      {currentDate.getFullYear()}
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled
                    className={`${styles["rmdp-arrow-container"]} ${styles["rmdp-right"]}`}
                    onClick={nextMonth}
                  >
                    <i className={styles["rmdp-arrow"]}></i>
                  </button>
                </div>
              </div>

              <div className={styles["rmdp-day-picker"]}>
                <div className={styles["rmdp-week"]}>
                  <div className={styles["rmdp-week-day"]}>Sun</div>
                  <div className={styles["rmdp-week-day"]}>Mon</div>
                  <div className={styles["rmdp-week-day"]}>Tue</div>
                  <div className={styles["rmdp-week-day"]}>Wed</div>
                  <div className={styles["rmdp-week-day"]}>Thu</div>
                  <div className={styles["rmdp-week-day"]}>Fri</div>
                  <div className={styles["rmdp-week-day"]}>Sat</div>
                </div>
                <div id="daysContainer">{renderCalendar()}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
Multipledatepicker.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func,
  label: PropTypes.string,
};
export default memo(Multipledatepicker);



